<template>
  <div class="partner-dashboard">
    <!-- Animated Background -->
    <div class="background-animation">
      <div class="shape shape-1"></div>
      <div class="shape shape-2"></div>
      <div class="shape shape-3"></div>
    </div>

    <!-- Header -->
    <header class="dashboard-header">
      <div class="user-profile">
        <div class="avatar">
          <span>{{ userInitials }}</span>
        </div>
        <div class="user-info">
          <div class="username">{{ user.username }}</div>
          <div class="user-role" :class="roleClass">
            {{ userRole }}
          </div>
        </div>
      </div>
    </header>

    <!-- Loading State -->
    <div v-if="loading" class="loading-overlay">
      <div class="loader">
        <div class="spinner"></div>
        <p>Loading your commission data...</p>
      </div>
    </div>

    <!-- Main Content -->
    <main v-else class="dashboard-content">
      <!-- Marketer Dashboard Section -->
      <section v-if="isMarketer" class="marketer-dashboard">
        <div class="section-header">
          <h2>Your <span>Marketer</span> Dashboard</h2>
          <p class="section-subtitle">Track your clients, commissions, and earnings</p>
        </div>

        <!-- Stats Cards -->
        <div class="stats-container">
          <div class="stat-card" v-for="stat in marketerStats" :key="stat.title">
            <div class="stat-icon" :style="{ background: stat.color }">
              <i :class="stat.icon"></i>
            </div>
            <div class="stat-content">
              <div class="stat-title">{{ stat.title }}</div>
              <div class="stat-value">{{ stat.value }}</div>
            </div>
          </div>
        </div>

        <!-- Advanced Dashboard Link -->
        <div class="advanced-dashboard-card">
          <div class="card-content">
            <div class="card-icon">
              <i class="fas fa-chart-network"></i>
            </div>
            <div class="card-text">
              <h3>Advanced Partner Dashboard</h3>
              <p>Access detailed analytics, performance metrics, and advanced commission tracking</p>
            </div>
          </div>
          <button class="btn btn-primary" @click="goToPartnerDashboard">
            Go to Advanced Dashboard <i class="fas fa-arrow-right"></i>
          </button>
        </div>

        <!-- Clients Section -->
        <div class="clients-section">
          <div class="section-title-row">
            <h3>Your Clients</h3>
            <div v-if="clients.length > 5" class="view-all" @click="openClientModal">View All</div>
          </div>

          <div v-if="clients.length > 0" class="client-list">
            <div v-for="(client, index) in clients.slice(0, 5)" :key="client.id" class="client-card">
              <div class="client-avatar">
                {{ client.initials }}
                <div class="status-indicator" :class="{active: client.active}"></div>
              </div>
              <div class="client-info">
                <div class="client-name">{{ client.name }}</div>
                <div class="client-details">
                  <div class="detail">
                    <div class="balance-indicator">
                      <div class="balance-icon" :class="{'active-balance': client.active}">
                        <i class="fas fa-wallet"></i>
                      </div>
                      <div class="balance-animation" v-if="client.active"></div>
                    </div>
                    <span class="balance-status">{{ client.active ? 'Active' : 'Developing' }}</span>
                  </div>
                  <div class="detail">
                    <i class="fas fa-coins"></i>
                    <span>${{ client.commission.toLocaleString() }} FCFA</span>
                  </div>
                </div>
              </div>
              <div class="client-actions">
                <button class="action-btn"><i class="fas fa-envelope"></i></button>
                <button class="action-btn"><i class="fas fa-chart-line"></i></button>
              </div>
            </div>
          </div>

          <div v-else class="empty-state">
            <div class="empty-icon">
              <i class="fas fa-user-plus"></i>
            </div>
            <h4>No Clients Yet</h4>
            <p>You haven't referred any clients yet. Start inviting to earn commissions.</p>
            <button class="btn btn-secondary" @click="openShareMenu">
              <i class="fas fa-user-plus"></i> Invite Clients
            </button>
          </div>
        </div>

        <div class="withdrawal-section">
          <button class="btn btn-withdraw" @click="goToWithdrawal">
            <i class="fas fa-money-bill-wave"></i> Request Withdrawal
          </button>
        </div>

        <!-- Transfer Section -->
        <div class="transfer-section">
          <div class="section-title-row">
            <h3>Transfer Commission</h3>
            <div class="transfer-info">Available: <span>${{ marketerStats[1].value }}</span></div>
          </div>

          <div class="transfer-form">
            <div class="form-group">
              <label>Amount to Transfer</label>
              <div class="input-with-icon">
                <i class="fas fa-dollar-sign"></i>
                <input
                  type="number"
                  v-model="transferAmount"
                  placeholder="0.00"
                  min="1"
                  :max="marketerStats[1].value"
                  step="0.01"
                >
              </div>
              <div class="max-amount" @click="transferAmount = marketerStats[1].value">
                Transfer Max
              </div>
            </div>

            <div class="form-group">
              <label>Password Confirmation</label>
              <div class="input-with-icon">
                <i class="fas fa-lock"></i>
                <input
                  type="password"
                  v-model="transferPassword"
                  placeholder="Enter your password"
                >
              </div>
            </div>

            <button class="btn btn-primary" @click="transferMarketerMoney" :disabled="transferLoading">
              <span v-if="transferLoading">
                <i class="fas fa-spinner fa-spin"></i> Processing...
              </span>
              <span v-else>
                <i class="fas fa-exchange-alt"></i> Transfer to Principal Account
              </span>
            </button>
            <div v-if="transferSuccess" class="success-message">
              <i class="fas fa-check-circle"></i> {{ transferSuccess }}
            </div>

            <div v-if="transferError" class="error-message">
              <i class="fas fa-exclamation-circle"></i> {{ transferError }}
            </div>
          </div>
        </div>
      </section>

      <!-- Regular User Commission Section -->
      <section v-else class="regular-user-dashboard">
        <div class="section-header">
          <h2>Your <span>Referral</span> Commission</h2>
          <p class="section-subtitle">Earn commissions by referring friends to our platform</p>
        </div>

        <div class="commission-card">
          <div class="card-content">
            <div class="card-icon">
              <i class="fas fa-gift"></i>
            </div>
            <div class="card-text">
              <div class="commission-amount">
                ${{ referralCommission.toLocaleString() }}
              </div>
              <p class="commission-info">
                Available to transfer to your account
              </p>
            </div>
            <div class="card-graph">
              <div class="graph-bar" :style="{height: `${referralCommission / 500 * 100}%`}"></div>
              <div class="graph-label">Earnings</div>
            </div>
          </div>
        </div>

        <div class="transfer-form">
          <div class="form-group">
            <label>Password Confirmation</label>
            <div class="input-with-icon">
              <i class="fas fa-lock"></i>
              <input
                type="password"
                v-model="transferPassword"
                placeholder="Enter your password"
              >
            </div>
          </div>

          <button class="btn btn-primary" @click="transferReferralCommission" :disabled="transferLoading">
            <span v-if="transferLoading">
              <i class="fas fa-spinner fa-spin"></i> Processing...
            </span>
            <span v-else>
              <i class="fas fa-exchange-alt"></i> Transfer ${{ referralCommission.toLocaleString() }} to your Principal Account
            </span>
          </button>

          <div v-if="transferSuccess" class="alert alert-success mt-3">
            <i class="fas fa-check-circle"></i> {{ transferSuccess }}
          </div>

          <div v-if="transferError" class="alert alert-danger mt-3">
            <i class="fas fa-exclamation-circle"></i> {{ transferError }}
          </div>
        </div>

        <div class="referred-users-section">
          <div class="section-title-row">
            <h3>Your Referred Users</h3>
            <div class="view-all" @click="openReferralModal">View All</div>
          </div>

          <div v-if="referredUsers.length > 0" class="user-list">
            <div v-for="(user, index) in referredUsers.slice(0, 5)" :key="user.id" class="referred-user">
              <div class="user-avatar">
                {{ user.initials }}
                <div class="status-indicator" :class="{active: user.savings >= 2000}"></div>
              </div>
              <div class="user-details">
                <div class="user-name">{{ user.name }}</div>
                <div class="user-status">
                  <div class="status-badge" :class="{active: user.savings >= 2000}">
                    {{ user.savings >= 2000 ? 'Active' : 'Developing' }}
                  </div>
                </div>
              </div>
              <div class="user-commission">
                <div class="commission-label">Your Commission</div>
                <div class="commission-value">${{ user.commission.toLocaleString() }}</div>
              </div>
            </div>
          </div>

          <div v-else class="empty-state">
            <div class="empty-icon">
              <i class="fas fa-users"></i>
            </div>
            <h4>No Referred Users Yet</h4>
            <p>You haven't referred any users yet. Start inviting friends to earn commissions.</p>
            <button class="btn btn-secondary" @click="openShareMenu">
              <i class="fas fa-user-plus"></i> Invite Friends
            </button>
          </div>
        </div>
      </section>
    </main>

    <!-- Client Modal -->
    <div v-if="showClientModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>All Clients</h3>
          <button @click="showClientModal = false" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div v-for="client in allClients" :key="client.id" class="modal-item">
            <div class="item-avatar">
              {{ client.initials }}
              <div class="status-indicator" :class="{active: client.active}"></div>
            </div>
            <div class="item-info">
              <div class="item-name">{{ client.name }}</div>
              <div class="item-details">
                <div class="detail">
                  <div class="balance-indicator">
                    <div class="balance-icon" :class="{'active-balance': client.active}">
                      <i class="fas fa-wallet"></i>
                    </div>
                    <div class="balance-animation" v-if="client.active"></div>
                  </div>
                  <span class="balance-status">{{ client.active ? 'Active' : 'Developing' }}</span>
                </div>
                <div class="detail">
                  <i class="fas fa-coins"></i>
                  <span>{{ client.commission }} FCFA</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Referral Modal -->
    <div v-if="showReferralModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>All Referred Users</h3>
          <button @click="showReferralModal = false" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div v-for="user in allReferredUsers" :key="user.id" class="modal-item">
            <div class="item-avatar">
              {{ user.initials }}
              <div class="status-indicator" :class="{active: user.savings >= 2000}"></div>
            </div>
            <div class="item-info">
              <div class="item-name">{{ user.name }}</div>
              <div class="item-details">
                <div class="detail">
                  <div class="balance-indicator">
                    <div class="balance-icon" :class="{'active-balance': user.savings >= 2000}">
                      <i class="fas fa-wallet"></i>
                    </div>
                    <div class="balance-animation" v-if="user.savings >= 2000"></div>
                  </div>
                  <span class="balance-status">{{ user.savings >= 2000 ? 'Active' : 'Developing' }}</span>
                </div>
                <div class="detail">
                  <i class="fas fa-gift"></i>
                  <span>{{ user.commission }} FCFA</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <footer class="dashboard-footer">
      <div class="footer-links">
        <a href="#"><i class="fas fa-question-circle"></i> Help Center</a>
        <a href="#"><i class="fas fa-file-contract"></i> Terms</a>
        <a href="#"><i class="fas fa-shield-alt"></i> Privacy</a>
      </div>
    </footer>

    <!-- Share Menu -->
    <div v-if="showShareMenu" class="share-menu" :style="shareMenuStyle" @click.stop>
      <div class="share-menu-header">
        <span>Share referral link</span>
        <button class="close-menu-btn" @click="showShareMenu = false">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="share-menu-options">
        <button class="share-option" @click="shareViaWhatsApp">
          <i class="fab fa-whatsapp" style="color: #25D366;"></i>
          <span>WhatsApp</span>
        </button>
        <button class="share-option" @click="shareViaFacebook">
          <i class="fab fa-facebook-f" style="color: #1877F2;"></i>
          <span>Facebook</span>
        </button>
        <button class="share-option" @click="shareViaSMS">
          <i class="fas fa-sms" style="color: #FF9800;"></i>
          <span>SMS</span>
        </button>
        <button class="share-option" @click="copyReferralLink">
          <i class="fas fa-copy" style="color: #0C49C2;"></i>
          <span>Copy Link</span>
        </button>
      </div>
    </div>

    <!-- Referral Info Popup (Explanatory) -->
    <ReferralInfoPopup v-if="!loading" />
  </div>
</template>

<script>
import axios from 'axios';
import ReferralInfoPopup from '@/components/ReferralEarningsPopup.vue'; // Assurez-vous que le chemin est correct

export default {
  name: 'PartnerDashboard',
  components: {
    ReferralInfoPopup
  },
  data() {
    return {
      loading: true,
      isMarketer: false,
      user: {
        id: null,
        username: '',
        firstName: '',
      },
      referralCommission: 0,
      transferAmount: 0,
      transferPassword: '',
      transferLoading: false,
      transferError: null,
      transferSuccess: null,
      marketerStats: [
        { title: "Total Clients", value: "0", icon: "fas fa-users", color: "linear-gradient(135deg, #0C49C2, #3A6BFF)" },
        { title: "Available Commission", value: "0.00", icon: "fas fa-coins", color: "linear-gradient(135deg, #FF6B35, #FF8E5E)" },
        { title: "Principal Balance", value: "0.00", icon: "fas fa-wallet", color: "linear-gradient(135deg, #00C9A7, #00E3B8)" }
      ],
      clients: [],
      referredUsers: [],
      showClientModal: false,
      showReferralModal: false,
      allClients: [],
      allReferredUsers: [],
      showShareMenu: false,
      shareMenuStyle: {
        top: '0px',
        left: '0px'
      }
    }
  },
  computed: {
    userInitials() {
      if (this.user.firstName && this.user.username) {
        return (this.user.firstName[0] + this.user.username[0]).toUpperCase();
      }
      return 'JD';
    },
    userRole() {
      return this.isMarketer ? 'Marketer' : 'Regular User';
    },
    roleClass() {
      return this.isMarketer ? 'role-marketer' : 'role-regular';
    }
  },
  methods: {
    // ========== MÉTHODES DE PARRAINAGE (LIEN ENCODÉ) ==========
    encodeReferralCode(serialNo) {
      if (!serialNo) return '';
      try {
        const payload = `pc_ref:${serialNo}`;
        return btoa(payload)
          .replace(/\+/g, '-')
          .replace(/\//g, '_')
          .replace(/=+$/, '');
      } catch (e) {
        console.error('Encode error:', e);
        return '';
      }
    },

    getReferralLink() {
      const baseUrl = 'https://myapp.pettycashglobal.com'; // ou window.location.origin selon l'environnement
      const serialNo = this.$store.state.user_info?.user_profile?.serial_no;
      const code = this.encodeReferralCode(serialNo);
      return `${baseUrl}/create_business_account2?ref=${code}`;
    },

    openShareMenu(event) {
      const rect = event.currentTarget.getBoundingClientRect();
      this.shareMenuStyle = {
        top: `${rect.bottom + window.scrollY + 5}px`,
        left: `${rect.left + window.scrollX}px`
      };
      this.showShareMenu = true;

      setTimeout(() => {
        window.addEventListener('click', this.closeShareMenuOnClickOutside);
      }, 100);
    },

    closeShareMenuOnClickOutside(event) {
      if (!event.target.closest('.share-menu') && !event.target.closest('.btn-secondary')) {
        this.showShareMenu = false;
        window.removeEventListener('click', this.closeShareMenuOnClickOutside);
      }
    },

    shareViaWhatsApp() {
      const link = this.getReferralLink();
      const serialNo = this.$store.state.user_info?.user_profile?.serial_no || '';
      const message = `🌟 Join PettyCash and earn commissions!\n\nUse my referral code *${serialNo}* to sign up:\n${link}\n\n_PettyCash — Fintech Made in Cameroon_ 🇨🇲`;
      window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
      this.showShareMenu = false;
    },

    shareViaFacebook() {
      const link = this.getReferralLink();
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`, '_blank', 'width=600,height=400');
      this.showShareMenu = false;
    },

    shareViaSMS() {
      const link = this.getReferralLink();
      const serialNo = this.$store.state.user_info?.user_profile?.serial_no || '';
      const body = `Join PettyCash with my code ${serialNo}: ${link}`;
      window.open(`sms:?body=${encodeURIComponent(body)}`);
      this.showShareMenu = false;
    },

    copyReferralLink() {
      const link = this.getReferralLink();
      navigator.clipboard.writeText(link).then(() => {
        this.$store.commit('setSnackBarMessage', 'Referral link copied! Share it with your friends.');
        this.$store.commit('setSnackBarColor', 'success darken-2');
        this.$store.commit('activateSnackBar', true);
      });
      this.showShareMenu = false;
    },

    // ========== MÉTHODES EXISTANTES ==========
    async fetchUserInfo() {
      try {
        const response = await axios.get('/api/v1/home/user-info2/');
        this.user = response.data;
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    },

    goToWithdrawal() {
      this.$router.push('/dashboard/affiliateMarketing/withdrawal');
    },

    goToPartnerDashboard() {
      this.$router.push('/dashboard/affiliateMarketing');
    },

    async checkMarketerStatus() {
      try {
        const response = await axios.get('/api/v1/home/check-marketer/');
        this.isMarketer = response.data.is_marketer;

        if (this.isMarketer) {
          await this.loadMarketerData();
        } else {
          await this.loadRegularUserData();
        }

        this.loading = false;
      } catch (error) {
        console.error('Error checking marketer status:', error);
        this.loading = false;
      }
    },

    async loadMarketerData() {
      try {
        const response = await axios.get('/api/v1/home/marketer-dashboard/');
        const availableCommission = response.data.available_commission;

        this.marketerStats = [
          {
            title: "Total Clients",
            value: response.data.total_clients.toString(),
            icon: "fas fa-users",
            color: "linear-gradient(135deg, #0C49C2, #3A6BFF)"
          },
          {
            title: "Available Commission",
            value: availableCommission.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }),
            icon: "fas fa-coins",
            color: "linear-gradient(135deg, #FF6B35, #FF8E5E)"
          },
          {
            title: "Principal Balance",
            value: response.data.principal_balance.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }),
            icon: "fas fa-wallet",
            color: "linear-gradient(135deg, #00C9A7, #00E3B8)"
          }
        ];

        this.clients = [
          ...response.data.clients_high,
          ...response.data.clients_low
        ].map(client => {
          const active = client.savings >= 2000;
          return {
            ...client,
            initials: client.name.split(' ').map(n => n[0]).join('').slice(0, 2),
            active: active
          };
        });
      } catch (error) {
        console.error('Error loading marketer data:', error);
      }
    },

    async transferMarketerMoney() {
      if (!this.transferPassword) {
        this.transferError = 'Please enter your password';
        return;
      }

      if (this.transferAmount <= 0) {
        this.transferError = 'Please enter a valid amount';
        return;
      }

      if (this.transferAmount > parseFloat(this.marketerStats[1].value.replace(/,/g, ''))) {
        this.transferError = 'Amount exceeds available commission';
        return;
      }

      this.transferLoading = true;
      this.transferError = null;

      try {
        const response = await axios.post('/api/v1/home/transfer/marketer/', {
          password: this.transferPassword,
          amount: this.transferAmount
        });

        if (response.data.success) {
          const transferredAmount = response.data.transferred_amount;
          this.transferSuccess = `${transferredAmount} FCFA has been transferred to your principal account`;
          await this.loadMarketerData();
          this.transferAmount = 0;
          this.transferPassword = '';
        } else {
          this.transferError = response.data.error || 'Transfer failed';
        }
      } catch (error) {
        if (error.response && error.response.data && error.response.data.error) {
          this.transferError = error.response.data.error;
        } else {
          this.transferError = 'An error occurred during transfer';
        }
      } finally {
        this.transferLoading = false;
      }
    },

    async loadRegularUserData() {
      try {
        const response = await axios.get('/api/v1/home/user-referral-dashboard/');
        this.referralCommission = response.data.referral_commission;
        this.referredUsers = response.data.referred_users.map(user => {
          return {
            ...user,
            initials: user.name.split(' ').map(n => n[0]).join('').slice(0, 2),
          };
        });
      } catch (error) {
        console.error('Error loading regular user data:', error);
      }
    },

    async openClientModal() {
      try {
        const response = await axios.get('/api/v1/home/all-clients/');
        this.allClients = response.data.map(client => {
          const active = client.savings >= 2000;
          return {
            ...client,
            initials: client.name.split(' ').map(n => n[0]).join('').slice(0, 2),
            active: active
          };
        });
        this.showClientModal = true;
      } catch (error) {
        console.error('Error loading all clients:', error);
      }
    },

    async openReferralModal() {
      try {
        const response = await axios.get('/api/v1/home/all-referred-users/');
        this.allReferredUsers = response.data.map(user => {
          return {
            ...user,
            initials: user.name.split(' ').map(n => n[0]).join('').slice(0, 2),
          };
        });
        this.showReferralModal = true;
      } catch (error) {
        console.error('Error loading all referred users:', error);
      }
    },

    async transferReferralCommission() {
      if (!this.transferPassword) {
        this.transferError = 'Please enter your password';
        return;
      }

      this.transferLoading = true;
      this.transferError = null;
      this.transferSuccess = null;

      try {
        const response = await axios.post('/api/v1/home/transfer/referral/', {
          password: this.transferPassword
        });

        if (response.data.success) {
          this.transferPassword = '';
          this.transferSuccess = response.data.message;
          setTimeout(() => {
            this.transferSuccess = null;
          }, 5000);
          await this.loadRegularUserData();
        } else {
          this.transferError = response.data.error || 'Transfer failed';
        }
      } catch (error) {
        if (error.response?.status === 400) {
          this.transferError = error.response.data.error || 'No commission available';
        } else if (error.response?.data?.error) {
          this.transferError = error.response.data.error;
        } else {
          this.transferError = 'An error occurred during transfer';
        }
      } finally {
        this.transferLoading = false;
      }
    },
  },
  async mounted() {
    await this.fetchUserInfo();
    await this.checkMarketerStatus();
  }
}
</script>

<style scoped>


* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.partner-dashboard {
  max-width: 1400px;
  margin: 0 auto;
  padding: 30px;
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  z-index: 1;
}

/* Animated Background */
.background-animation {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  overflow: hidden;
}

.shape {
  position: absolute;
  border-radius: 50%;
  opacity: 0.1;
}

.shape-1 {
  width: 600px;
  height: 600px;
  background: linear-gradient(135deg, #0C49C2, #3A6BFF);
  top: -300px;
  right: -300px;
  animation: float 25s infinite linear;
}

.shape-2 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #FF6B35, #FF8E5E);
  bottom: -200px;
  left: -200px;
  animation: float 30s infinite linear reverse;
}

.shape-3 {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, #00C9A7, #00E3B8);
  top: 50%;
  left: 10%;
  animation: float 20s infinite linear;
}

@keyframes float {
  0% {
    transform: translate(0, 0) rotate(0deg);
  }
  25% {
    transform: translate(20px, 20px) rotate(90deg);
  }
  50% {
    transform: translate(0, 40px) rotate(180deg);
  }
  75% {
    transform: translate(-20px, 20px) rotate(270deg);
  }
  100% {
    transform: translate(0, 0) rotate(360deg);
  }
}

/* Header */
.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
  margin-bottom: 30px;
}

.logo-container {
  display: flex;
  align-items: center;
  gap: 15px;
}

.logo-icon {
  width: 60px;
  height: 60px;
  border-radius: 18px;
  background: linear-gradient(135deg, #0C49C2, #3A6BFF);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 20px rgba(12, 73, 194, 0.3);
}

.logo-inner {
  width: 44px;
  height: 44px;
  background: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0C49C2;
  font-size: 20px;
}

.logo-text h1 {
  font-size: 28px;
  font-weight: 700;
  color: #1A1F36;
}

.logo-text h1 span {
  color: #0C49C2;
  font-weight: 800;
}

.logo-text p {
  font-size: 14px;
  color: #6C757D;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.user-profile {
  display: flex;
  align-items: center;
  gap: 15px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  padding: 10px 20px;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(12, 73, 194, 0.1);
}

.avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0C49C2, #3A6BFF);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 18px;
  box-shadow: 0 5px 10px rgba(12, 73, 194, 0.2);
}

.user-info {
  display: flex;
  flex-direction: column;
}

.username {
  font-weight: 600;
  color: #1A1F36;
}

.user-role {
  font-size: 14px;
  padding: 4px 12px;
  border-radius: 20px;
  background: rgba(12, 73, 194, 0.1);
  color: #0C49C2;
  display: inline-block;
  margin-top: 4px;
  font-weight: 500;
}

.role-marketer {
  background: rgba(255, 107, 53, 0.1);
  color: #FF6B35;
}

/* Loading Overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
}

.loader {
  text-align: center;
}

.spinner {
  width: 60px;
  height: 60px;
  border: 5px solid rgba(12, 73, 194, 0.1);
  border-top: 5px solid #0C49C2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loader p {
  font-size: 18px;
  color: #1A1F36;
  font-weight: 500;
}

/* Main Content */
.dashboard-content {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 25px;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.05);
  padding: 40px;
  margin-bottom: 30px;
  border: 1px solid rgba(12, 73, 194, 0.1);
}

.section-header {
  margin-bottom: 40px;
}

.section-header h2 {
  font-size: 36px;
  color: #1A1F36;
  margin-bottom: 10px;
  font-weight: 700;
}

.section-header h2 span {
  color: #0C49C2;
  font-weight: 800;
}

.section-subtitle {
  font-size: 18px;
  color: #6C757D;
  max-width: 700px;
  line-height: 1.7;
}

/* Stats Cards */
.stats-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 25px;
  margin-bottom: 40px;
}

.stat-card {
  background: white;
  border-radius: 20px;
  padding: 30px;
  display: flex;
  align-items: center;
  gap: 20px;
  transition: all 0.4s ease;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(12, 73, 194, 0.05);
  position: relative;
  overflow: hidden;
}

.stat-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 15px 35px rgba(12, 73, 194, 0.15);
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 5px;
}

.stat-icon {
  width: 70px;
  height: 70px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 28px;
  flex-shrink: 0;
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
}

.stat-content {
  flex: 1;
}

.stat-title {
  font-size: 16px;
  color: #6C757D;
  margin-bottom: 8px;
  font-weight: 500;
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  color: #1A1F36;
}

/* Advanced Dashboard Card */
.advanced-dashboard-card {
  background: linear-gradient(135deg, rgba(12, 73, 194, 0.05), rgba(255, 255, 255, 0.8));
  border-radius: 20px;
  padding: 30px;
  margin-bottom: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid rgba(12, 73, 194, 0.1);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.03);
  transition: all 0.4s ease;
}

.advanced-dashboard-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px rgba(12, 73, 194, 0.15);
}

.card-content {
  display: flex;
  align-items: center;
  gap: 25px;
}

.card-icon {
  width: 70px;
  height: 70px;
  border-radius: 18px;
  background: linear-gradient(135deg, #0C49C2, #3A6BFF);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 28px;
  box-shadow: 0 8px 15px rgba(12, 73, 194, 0.3);
}

.card-text h3 {
  font-size: 24px;
  color: #1A1F36;
  margin-bottom: 10px;
  font-weight: 700;
}

.card-text p {
  color: #6C757D;
  max-width: 500px;
  line-height: 1.7;
}

/* Buttons */
.btn {
  padding: 16px 35px;
  border-radius: 15px;
  border: none;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  white-space: nowrap;
}

.btn-primary {
  background: linear-gradient(135deg, #0C49C2, #3A6BFF);
  color: white;
  box-shadow: 0 8px 20px rgba(12, 73, 194, 0.3);
}

.btn-primary:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 25px rgba(12, 73, 194, 0.4);
  background: linear-gradient(135deg, #0A3FB0, #2E5AE6);
}

.btn-secondary {
  background: white;
  color: #0C49C2;
  border: 2px solid #0C49C2;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
}

.btn-secondary:hover {
  background: rgba(12, 73, 194, 0.05);
  transform: translateY(-3px);
}

/* Clients Section */
.clients-section {
  margin-top: 40px;
}

.section-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
}

.section-title-row h3 {
  font-size: 22px;
  color: #1A1F36;
  font-weight: 700;
}

.view-all {
  color: #0C49C2;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.view-all:hover {
  text-decoration: underline;
}

.client-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
}

.client-card {
  background: white;
  border-radius: 20px;
  padding: 25px;
  display: flex;
  align-items: center;
  gap: 20px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
  transition: all 0.4s ease;
  border: 1px solid rgba(12, 73, 194, 0.05);
}

.client-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px rgba(12, 73, 194, 0.1);
}

.client-avatar {
  width: 60px;
  height: 60px;
  border-radius: 18px;
  background: linear-gradient(135deg, #0C49C2, #3A6BFF);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 20px;
  position: relative;
}

.status-indicator {
  position: absolute;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #6C757D;
  border: 2px solid white;
  bottom: -3px;
  right: -3px;
}

.status-indicator.active {
  background: #00C9A7;
}

.client-info {
  flex: 1;
}

.client-name {
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 18px;
  color: #1A1F36;
}

.client-details {
  display: flex;
  gap: 20px;
}

.detail {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #6C757D;
}

.detail i {
  color: #0C49C2;
}

.client-actions {
  display: flex;
  gap: 12px;
}

.action-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgba(12, 73, 194, 0.05);
  border: none;
  color: #0C49C2;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.action-btn:hover {
  background: rgba(12, 73, 194, 0.1);
  transform: translateY(-3px);
}

/* Transfer Section */
.transfer-section {
  margin-top: 40px;
  background: white;
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(12, 73, 194, 0.05);
}

.transfer-info {
  color: #6C757D;
  font-size: 16px;
}

.transfer-info span {
  color: #0C49C2;
  font-weight: 600;
}

.transfer-form {
  margin-top: 20px;
}

.form-group {
  margin-bottom: 25px;
  position: relative;
}

label {
  display: block;
  margin-bottom: 10px;
  font-weight: 600;
  color: #1A1F36;
  font-size: 16px;
}

.input-with-icon {
  position: relative;
}

.input-with-icon i {
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  color: #6C757D;
  font-size: 18px;
}

.input-with-icon input {
  width: 100%;
  padding: 16px 20px 16px 50px;
  border: 2px solid #E9ECEF;
  border-radius: 15px;
  font-size: 16px;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
}

.input-with-icon input:focus {
  border-color: #0C49C2;
  outline: none;
  box-shadow: 0 0 0 4px rgba(12, 73, 194, 0.1);
}

.max-amount {
  position: absolute;
  right: 20px;
  top: 50px;
  color: #0C49C2;
  font-weight: 600;
  cursor: pointer;
  font-size: 14px;
}

.max-amount:hover {
  text-decoration: underline;
}

/* Commission Card */
.commission-card {
  background: linear-gradient(135deg, rgba(12, 73, 194, 0.05), rgba(255, 255, 255, 0.8));
  border-radius: 20px;
  padding: 40px;
  margin-bottom: 40px;
  border: 1px solid rgba(12, 73, 194, 0.1);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.03);
}

.card-content {
  display: flex;
  align-items: center;
  gap: 30px;
}

.card-icon {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: linear-gradient(135deg, #0C49C2, #3A6BFF);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 32px;
  box-shadow: 0 10px 20px rgba(12, 73, 194, 0.3);
}

.commission-amount {
  font-size: 52px;
  font-weight: 800;
  color: #1A1F36;
  margin-bottom: 10px;
  background: linear-gradient(135deg, #0C49C2, #3A6BFF);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.commission-info {
  font-size: 18px;
  color: #6C757D;
  max-width: 400px;
}

.card-graph {
  margin-left: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 50px;
  height: 150px;
}

.graph-bar {
  width: 30px;
  background: linear-gradient(to top, #0C49C2, #3A6BFF);
  border-radius: 10px 10px 0 0;
  transition: height 1s ease;
}

.graph-label {
  margin-top: 10px;
  font-size: 14px;
  color: #6C757D;
}

/* Referred Users */
.referred-user {
  background: white;
  border-radius: 20px;
  padding: 25px;
  display: flex;
  align-items: center;
  gap: 20px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
  transition: all 0.4s ease;
  border: 1px solid rgba(12, 73, 194, 0.05);
}

.referred-user:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px rgba(12, 73, 194, 0.1);
}

.user-avatar {
  width: 60px;
  height: 60px;
  border-radius: 18px;
  background: linear-gradient(135deg, #0C49C2, #3A6BFF);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 20px;
  position: relative;
}

.user-details {
  flex: 1;
}

.user-name {
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 18px;
  color: #1A1F36;
}

.user-status {
  display: flex;
  align-items: center;
  gap: 15px;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 20px;
  background: #E9ECEF;
  color: #6C757D;
  font-size: 14px;
  font-weight: 500;
}

.status-badge.active {
  background: rgba(0, 201, 167, 0.1);
  color: #00C9A7;
}

.user-savings {
  font-size: 14px;
  color: #6C757D;
}

.user-commission {
  text-align: right;
}

.commission-label {
  font-size: 14px;
  color: #6C757D;
  margin-bottom: 5px;
}

.commission-value {
  font-size: 20px;
  font-weight: 700;
  color: #FF6B35;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 50px 30px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
  margin-top: 30px;
  border: 1px solid rgba(12, 73, 194, 0.05);
}

.empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(12, 73, 194, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 25px;
  color: #0C49C2;
  font-size: 36px;
}

.empty-state h4 {
  font-size: 24px;
  color: #1A1F36;
  margin-bottom: 15px;
  font-weight: 700;
}

.empty-state p {
  font-size: 18px;
  color: #6C757D;
  margin-bottom: 30px;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
}

/* Error Message */
.error-message {
  margin-top: 20px;
  padding: 15px 20px;
  background: rgba(255, 107, 53, 0.1);
  border-radius: 15px;
  color: #FF6B35;
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 500;
  border: 1px solid rgba(255, 107, 53, 0.2);
}

/* Footer */
.dashboard-footer {
  text-align: center;
  padding: 30px 0;
  color: #6C757D;
  border-top: 1px solid rgba(12, 73, 194, 0.1);
}

.footer-links {
  display: flex;
  justify-content: center;
  gap: 30px;
  margin-top: 20px;
}

.footer-links a {
  color: #6C757D;
  text-decoration: none;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.footer-links a:hover {
  color: #0C49C2;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .stats-container {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }

  .client-list,
  .user-list {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  }
}

@media (max-width: 768px) {
  .dashboard-header {
    flex-direction: column;
    gap: 25px;
    text-align: center;
  }

  .user-profile {
    align-self: center;
  }

  .advanced-dashboard-card {
    flex-direction: column;
    gap: 25px;
    text-align: center;
  }

  .card-content {
    flex-direction: column;
    text-align: center;
  }

  .section-title-row {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }

  .client-card,
  .referred-user {
    flex-direction: column;
    text-align: center;
    gap: 15px;
  }

  .client-actions {
    justify-content: center;
  }

  .commission-card .card-content {
    flex-direction: column;
    text-align: center;
  }

  .card-graph {
    margin: 20px auto 0;
  }
}

@media (max-width: 480px) {
  .dashboard-content {
    padding: 25px;
  }

  .section-header h2 {
    font-size: 28px;
  }

  .stats-container {
    grid-template-columns: 1fr;
  }

  .transfer-form .btn {
    width: 100%;
  }

  .footer-links {
    flex-direction: column;
    gap: 15px;
  }
}


/* Withdrawal Button */
.withdrawal-section {
  margin-bottom: 20px;
  text-align: center;
}

.btn-withdraw {
  background: linear-gradient(135deg, #FF6B35, #FF8E5E);
  color: white;
  padding: 12px 25px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(255, 107, 53, 0.3);
  display: inline-flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
}

.btn-withdraw:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(255, 107, 53, 0.4);
}

/* Stats Cards */
.stats-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 15px;
  margin-bottom: 25px;
}

.stat-card {
  background: white;
  border-radius: 15px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(12, 73, 194, 0.05);
}

.stat-icon {
  width: 55px;
  height: 55px;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
  flex-shrink: 0;
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
}

.stat-content {
  flex: 1;
}

.stat-title {
  font-size: 14px;
  color: #6C757D;
  margin-bottom: 5px;
  font-weight: 500;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #1A1F36;
}

/* Sections */
.section-header {
  margin-bottom: 25px;
}

.section-header h2 {
  font-size: 26px;
  color: #1A1F36;
  margin-bottom: 8px;
  font-weight: 700;
}

.section-header h2 span {
  color: #0C49C2;
  font-weight: 800;
}

.section-subtitle {
  font-size: 16px;
  color: #6C757D;
  line-height: 1.6;
}

/* Cards and Lists */
.client-list, .user-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 15px;
}

.client-card, .referred-user {
  background: white;
  border-radius: 15px;
  padding: 18px;
  display: flex;
  align-items: center;
  gap: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(12, 73, 194, 0.05);
}

.client-avatar, .user-avatar {
  width: 50px;
  height: 50px;
  border-radius: 15px;
  background: linear-gradient(135deg, #0C49C2, #3A6BFF);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 18px;
  position: relative;
}

.status-indicator {
  position: absolute;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #6C757D;
  border: 2px solid white;
  bottom: -2px;
  right: -2px;
}

.status-indicator.active {
  background: #00C9A7;
}

/* Modals */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 15px;
}

.modal-content {
  background: white;
  border-radius: 15px;
  width: 100%;
  max-width: 500px;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.4rem;
  color: #0C49C2;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.4rem;
  cursor: pointer;
  color: #6C757D;
  padding: 5px;
}

.modal-body {
  padding: 15px;
}

.modal-item {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f5f5f5;
}

.item-avatar {
  width: 45px;
  height: 45px;
  border-radius: 12px;
  background: linear-gradient(135deg, #0C49C2, #3A6BFF);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 16px;
  margin-right: 12px;
  position: relative;
}

.item-info {
  flex-grow: 1;
}

.item-name {
  font-weight: 600;
  margin-bottom: 5px;
  font-size: 16px;
}

.item-details {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.detail {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #6C757D;
}

.detail i {
  color: #0C49C2;
  font-size: 14px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .partner-dashboard {
    padding: 15px;
  }

  .dashboard-content {
    padding: 15px;
  }

  .section-header h2 {
    font-size: 22px;
  }

  .stats-container {
    grid-template-columns: 1fr;
  }

  .client-list, .user-list {
    grid-template-columns: 1fr;
  }

  .client-card, .referred-user {
    flex-direction: column;
    text-align: center;
    gap: 12px;
    padding: 15px;
  }

  .client-details, .user-status {
    justify-content: center;
  }

  .transfer-form .btn, .btn-withdraw {
    width: 100%;
    padding: 14px;
  }

  .modal-content {
    max-height: 80vh;
  }
}

@media (max-width: 480px) {
  .user-profile {
    padding: 8px 12px;
  }

  .avatar {
    width: 40px;
    height: 40px;
    font-size: 14px;
  }

  .username {
    font-size: 14px;
  }

  .user-role {
    font-size: 11px;
    padding: 2px 8px;
  }

  .section-header h2 {
    font-size: 20px;
  }

  .section-subtitle {
    font-size: 14px;
  }

  .stat-card {
    padding: 15px;
  }

  .stat-icon {
    width: 45px;
    height: 45px;
    font-size: 20px;
  }

  .stat-title {
    font-size: 13px;
  }

  .stat-value {
    font-size: 20px;
  }

  .footer-links {
    flex-direction: column;
    gap: 10px;
  }
}










@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}




.btn-primary {
  background: white;
  color: #3a6bff;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.btn-primary:hover {
  background: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15);
}

.btn-primary i {
  margin-left: 8px;
  transition: transform 0.3s ease;
}

.btn-primary:hover i {
  transform: translateX(3px);
}

.btn-withdraw {
  background: linear-gradient(135deg, #ff6b35, #ff8e5e);
  color: white;
  width: 100%;
  padding: 15px;
  font-size: 16px;
  margin-top: 20px;
}

.btn-withdraw:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(255, 107, 53, 0.3);
}

.btn-secondary {
  background: #f0f4f8;
  color: #3a6bff;
  padding: 10px 20px;
  margin-top: 15px;
}

.btn-secondary:hover {
  background: #e4eaf1;
}

.section-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-title-row h3 {
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
}

.view-all {
  color: #3a6bff;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.3s ease;
}

.view-all:hover {
  color: #0c49c2;
  transform: translateX(3px);
}

.view-all::after {
  content: '→';
}


.status-indicator {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ddd;
  border: 2px solid white;
}

.status-indicator.active {
  background: #00c9a7;
}

.client-info, .user-details {
  flex: 1;
}

.client-name, .user-name {
  font-weight: 600;
  margin-bottom: 8px;
  color: #2c3e50;
}

.client-details, .item-details {
  display: flex;
  gap: 15px;
}

.detail {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  color: #666;
}

.balance-indicator {
  position: relative;
  display: flex;
  align-items: center;
}

.balance-icon {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #f0f4f8;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #666;
}

.balance-icon.active-balance {
  background: #e3f7eb;
  color: #00a76f;
}

.balance-animation {
  position: absolute;
  top: -2px;
  left: -2px;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 2px solid #00c9a7;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  70% {
    transform: scale(1.5);
    opacity: 0;
  }
  100% {
    opacity: 0;
  }
}

.balance-status {
  font-weight: 500;
  margin-left: 5px;
}

.client-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #f0f4f8;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #666;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
}

.action-btn:hover {
  background: #3a6bff;
  color: white;
}

.empty-state {
  background: white;
  border-radius: 15px;
  padding: 30px;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
}

.empty-icon {
  font-size: 50px;
  color: #c5cbd6;
  margin-bottom: 20px;
}

.empty-state h4 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #2c3e50;
}

.empty-state p {
  color: #777;
  margin-bottom: 20px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}


.transfer-info {
  font-size: 15px;
  color: #666;
}

.transfer-info span {
  font-weight: 600;
  color: #00c9a7;
}

.transfer-form {
  margin-top: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #555;
}

.input-with-icon {
  position: relative;
}

.input-with-icon i {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
}

.input-with-icon input {
  width: 100%;
  padding: 12px 20px 12px 45px;
  border-radius: 10px;
  border: 1px solid #ddd;
  font-size: 15px;
  transition: all 0.3s ease;
}

.input-with-icon input:focus {
  border-color: #3a6bff;
  box-shadow: 0 0 0 3px rgba(58, 107, 255, 0.1);
  outline: none;
}

.max-amount {
  font-size: 14px;
  color: #3a6bff;
  margin-top: 8px;
  cursor: pointer;
  font-weight: 500;
  display: inline-block;
}

.max-amount:hover {
  text-decoration: underline;
}

.error-message {
  margin-top: 15px;
  padding: 10px 15px;
  background: #ffebee;
  border-radius: 8px;
  color: #c62828;
  display: flex;
  align-items: center;
  gap: 8px;
}

.commission-card {
  background: white;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  margin-bottom: 30px;
  position: relative;
  overflow: hidden;
}

.commission-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(0, 201, 167, 0.05), rgba(0, 227, 184, 0.05));
  z-index: 0;
}

.card-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
}

.commission-amount {
  font-size: 36px;
  font-weight: 700;
  color: #00c9a7;
  margin-bottom: 10px;
}

.commission-info {
  color: #666;
  font-size: 16px;
  max-width: 300px;
}

.card-graph {
  position: relative;
  width: 60px;
  height: 150px;
  background: #f0f4f8;
  border-radius: 30px;
  overflow: hidden;
  margin-left: auto;
}

.graph-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background: linear-gradient(to top, #00c9a7, #00e3b8);
  border-radius: 30px;
  transition: height 1s ease;
}

.graph-label {
  position: absolute;
  bottom: -25px;
  left: 0;
  width: 100%;
  text-align: center;
  font-size: 12px;
  color: #666;
}

.user-status {
  display: flex;
  align-items: center;
  gap: 10px;
}

.status-badge {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: #f0f4f8;
  color: #666;
}

.status-badge.active {
  background: #e3f7eb;
  color: #00a76f;
}

.user-commission {
  text-align: right;
}

.commission-label {
  font-size: 12px;
  color: #777;
  margin-bottom: 3px;
}

.commission-value {
  font-weight: 600;
  color: #ff6b35;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.modal-content {
  background: white;
  border-radius: 15px;
  width: 90%;
  max-width: 800px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.modal-header {
  padding: 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  font-size: 22px;
  font-weight: 600;
  color: #2c3e50;
}

.close-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #f0f4f8;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #666;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
}

.close-btn:hover {
  background: #3a6bff;
  color: white;
}

.modal-body {
  padding: 20px;
}

.modal-item {
  display: flex;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid #f0f4f8;
}

.modal-item:last-child {
  border-bottom: none;
}

.item-avatar {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: #f0f4f8;
  color: #0C49C2;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  font-size: 14px;
  position: relative;
  margin-right: 15px;
}

.item-info {
  flex: 1;
}

.item-name {
  font-weight: 600;
  margin-bottom: 5px;

}


.btn-primary {
  background-color: #0C49C2;
  border-color: #0C49C2;
  overflow: auto;

  font-size: 14px;
  font-weight: 600;

  white-space: normal;
  word-wrap: break-word;
  line-height: 1.4;
  padding: 10px 16px;
  text-align: center;
  max-width: 200px;
}

.alert {
  padding: 10px 15px;
  border-radius: 4px;
  margin-top: 10px;
}

.alert-success {
  background-color: #d4edda;
  border-color: #c3e6cb;
  color: #155724;
}

.alert-danger {
  background-color: #f8d7da;
  border-color: #f5c6cb;
  color: #721c24;
}

.fa-spinner {
  margin-right: 5px;
}
.transfer-icon-only {
  display: none;
}

@media (max-width: 400px) {
  .transfer-text,
  .transfer-short {
    display: none;
  }

  .transfer-icon-only {
    display: inline;
  }

  .btn {
    min-width: 40px; /* Garantir une taille minimale */
  }
}
/* Styles pour les listes limitées */
.client-list, .user-list {
  max-height: 400px;
  overflow-y: auto;
}

/* Styles pour les modales */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 10px;
  width: 90%;
  max-width: 800px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.modal-header {
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-body {
  padding: 20px;
}

.modal-item {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f5f5f5;
}

/* Bouton "View All" */
.view-all {
  color: #3498db;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s;
}

.view-all:hover {
  color: #1a5276;
  text-decoration: underline;
}

.share-menu {
  position: absolute;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  min-width: 220px;
  z-index: 1000;
  overflow: hidden;
  border: 1px solid rgba(12, 73, 194, 0.1);
}

.share-menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8faff;
  border-bottom: 1px solid #e9ecef;
  font-weight: 600;
  color: #1A1F36;
}

.close-menu-btn {
  background: none;
  border: none;
  color: #6C757D;
  cursor: pointer;
  font-size: 16px;
  padding: 0 4px;
}

.close-menu-btn:hover {
  color: #0C49C2;
}

.share-menu-options {
  padding: 8px 0;
}

.share-option {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  color: #1A1F36;
  transition: background 0.2s;
  text-align: left;
}

.share-option:hover {
  background: #f5f7fa;
}

.share-option i {
  font-size: 18px;
  width: 24px;
  text-align: center;
}

/* Base styles */
</style>